import { useEffect, useState } from "react";
import { Mail, Menu, X } from "lucide-react";
import { Carrot } from "@/components/Carrot";
import { CarrotIcon } from "@/components/CarrotIcon";
import { ChaseProgress } from "@/components/ChaseProgress";
import { useContactDialog } from "@/components/ContactDialog";
import { LOGIN_URL, SIGNUP_URL } from "@/lib/site";

// Absolute hrefs so every link works from any page. The `/#how` anchor lands
// on the home page and scrolls; the page links route to their own SSG route.
const NAV_LINKS = [
  { label: "How it works", href: "/#how" },
  { label: "About", href: "/about" },
  { label: "Team", href: "/team" },
] as const;

// `current` is the active route ("/", "/about", "/team"), passed in by the page
// so the highlight is correct in the pre-rendered HTML — no client-only guess.
export function SiteNav({ current }: { current?: string }) {
  const { openContact } = useContactDialog();
  const [menuOpen, setMenuOpen] = useState(false);

  // Keep the background still while the mobile sheet is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  // The scrolled shadow is a CSS scroll-driven animation (.nav-scrolled in
  // index.css): no window scroll listener, degrades to the border alone.
  return (
    <header className="nav-scrolled sticky top-0 z-50 border-b border-ink/10 bg-cream/80 backdrop-blur-md">
      <nav className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3.5 sm:px-8">
        <a href="/" className="group flex min-w-0 items-center gap-2.5" aria-label="CarrotOnTheHorse home">
          <Carrot className="h-6 w-6 shrink-0 transition-transform duration-300 group-hover:-rotate-12 sm:h-7 sm:w-7" />
          <span className="truncate font-display text-base font-bold tracking-tight text-ink sm:text-lg">
            Carrot<span className="text-carrot">OnThe</span>Horse
          </span>
        </a>

        {/* Desktop nav */}
        <div className="ml-auto hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => {
            const active = link.href === current;
            return (
              <a
                key={link.href}
                href={link.href}
                aria-current={active ? "page" : undefined}
                className={`rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                  active ? "bg-field-soft text-field-deep" : "text-ink/70 hover:text-carrot-deep"
                }`}
              >
                {link.label}
              </a>
            );
          })}
          <button
            type="button"
            onClick={openContact}
            className="rounded-full px-3.5 py-2 text-sm font-semibold text-ink/70 transition-colors hover:text-carrot-deep"
          >
            Get in touch
          </button>
        </div>

        {/* Desktop auth */}
        <div className="hidden items-center gap-2 lg:ml-3 lg:flex">
          <a
            href={LOGIN_URL}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-paper px-4 py-2 text-sm font-semibold text-ink transition-all hover:border-carrot hover:text-carrot-deep"
          >
            <CarrotIcon className="carrot-icon-brand h-[1.125rem] w-3.5" />
            Log in
          </a>
          <a
            href={SIGNUP_URL}
            className="action-button group inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold"
          >
            Sign up
            <CarrotIcon
              className="h-[1.125rem] w-3.5 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          className="ml-auto inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-ink/15 bg-paper text-ink transition-colors hover:border-carrot hover:text-carrot-deep lg:hidden"
        >
          {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </nav>

      {/* Mobile sheet */}
      {menuOpen && (
        <div className="coth-enter border-t border-ink/10 bg-cream/95 backdrop-blur-md lg:hidden">
          <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-4 sm:px-8">
            {NAV_LINKS.map((link) => {
              const active = link.href === current;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  aria-current={active ? "page" : undefined}
                  onClick={() => setMenuOpen(false)}
                  className={`rounded-xl px-3 py-3 text-base font-semibold transition-colors ${
                    active ? "bg-field-soft text-field-deep" : "text-ink hover:bg-carrot-soft/50 hover:text-carrot-deep"
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                openContact();
              }}
              className="flex items-center gap-2 rounded-xl px-3 py-3 text-left text-base font-semibold text-ink transition-colors hover:bg-carrot-soft/50 hover:text-carrot-deep"
            >
              <Mail className="size-4" />
              Get in touch
            </button>
            <div className="mt-3 grid grid-cols-2 gap-2.5">
              <a
                href={LOGIN_URL}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-ink/15 bg-paper px-4 py-3 text-sm font-semibold text-ink transition-all hover:border-carrot hover:text-carrot-deep"
              >
                <CarrotIcon className="carrot-icon-brand h-[1.125rem] w-3.5" />
                Log in
              </a>
              <a
                href={SIGNUP_URL}
                className="action-button inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-3 text-sm font-bold"
              >
                Sign up
                <CarrotIcon className="h-[1.125rem] w-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}

      <ChaseProgress />
    </header>
  );
}
